import { Camera, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import { useTestimonials } from "@/hooks/useProducts";
import { Reveal } from "@/components/site/Reveal";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { LazyAutoplayVideo } from "@/components/site/LazyAutoplayVideo";

function Card({ t, i }) {
  return (
    <Reveal delay={(i % 3) * 0.1} className="flex flex-col">
      <BeforeAfter before={t.before_image} after={t.after_image} alt={t.name} testid={`before-after-${i}`} />
      <div className="mt-6 flex flex-1 flex-col">
        {t.quote ? <Quote className="h-6 w-6 text-[var(--gold)]" /> : null}
        {t.quote ? <p data-testid={`testimonial-quote-${i}`} className="mt-4 font-display text-xl italic leading-snug text-[var(--parchment)]">« {t.quote} »</p> : null}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--line)] pt-5 text-sm">
          <span>
            <span className="text-[var(--parchment)]">{t.name}</span>
            {t.city ? <span className="text-[var(--muted)]"> · {t.city}</span> : null}
          </span>
          <span className="flex gap-2">
            {t.product ? <span className="rounded-full border border-[var(--line)] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[var(--gold-soft)]">{t.product}</span> : null}
            {t.duration ? <span className="rounded-full bg-[var(--plum)] px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-[var(--parchment)]">{t.duration}</span> : null}
          </span>
        </div>
      </div>
    </Reveal>
  );
}

const VIDEOS = [
  { src: "/results/video-avant-apres", label: "Sommet du crâne" },
  { src: "/results/video-avant-apres-2", label: "Tempes et côtés" },
];

function Video() {
  return (
    <div data-testid="testimonials-video" className="mt-16 grid items-center gap-8 md:grid-cols-[minmax(0,560px)_1fr]">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {VIDEOS.map((v, i) => (
          <figure key={v.src}>
            <LazyAutoplayVideo src={`${v.src}.mp4`} poster={`${v.src}.webp`} data-testid={`testimonials-video-${i}`}
              aria-label={`Vidéo avant / après d'une cliente Ozentya : ${v.label}`} className="mx-auto aspect-[9/16] w-full max-w-[320px] rounded-sm border border-[var(--line)] bg-black object-cover" />
            <figcaption className="mt-2 text-center text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)]">{v.label}</figcaption>
          </figure>
        ))}
      </div>
      <div>
        <p className="eyebrow mb-3">En vidéo</p>
        <p className="font-display text-3xl leading-tight md:text-4xl">Avant / après <span className="italic gold-text">de clientes Ozentya</span></p>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--parchment)]/85">Zones clairsemées au sommet du crâne, tempes et côtés dégarnis, puis évolution avec une routine Ozentya suivie avec régularité. Résultats individuels, variables selon chaque personne.</p>
      </div>
    </div>
  );
}

export function Testimonials({ className = "" }) {
  const { data } = useTestimonials();
  const list = Array.isArray(data) ? data : [];
  if (!list.length) return null;

  return (
    <section data-testid="testimonials-section" className={`relative bg-[var(--ink-2)] py-24 md:py-36 ${className}`}>
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="eyebrow mb-4">Avant / Après</p>
            <h2 className="font-display text-4xl leading-none md:text-6xl">Des résultats <span className="italic gold-text">qui se voient.</span></h2>
          </div>
          <p className="max-w-sm text-sm text-[var(--muted)] md:text-base">Photos réelles envoyées par nos clientes. Glissez le curseur pour révéler la repousse.</p>
        </Reveal>
        <div className={`grid grid-cols-1 gap-12 md:grid-cols-2 ${list.length > 2 ? "lg:grid-cols-3" : "lg:max-w-[1120px]"}`}>
          {list.map((t, i) => <Card key={t.id || i} t={t} i={i} />)}
        </div>
        <Video />
        <Link to="/partager-mon-avant-apres" data-testid="testimonials-share-link" className="mt-12 inline-flex h-12 items-center gap-2 rounded-full border border-[var(--gold)] px-6 text-xs uppercase tracking-[0.18em] text-[var(--gold-soft)] transition-colors hover:bg-[var(--gold)] hover:text-[var(--ink)]">
          <Camera className="h-4 w-4" aria-hidden="true" />Partagez votre avant / après · -15 % offerts
        </Link>
        <p data-testid="testimonials-disclaimer" className="mt-10 max-w-3xl text-xs leading-relaxed text-[var(--parchment)]/75">Photos partagées par nos clientes. Résultats individuels : ils dépendent de la cause de la chute, de la régularité de la routine et de chaque personne, et ne sont pas garantis. Nos produits sont des soins cosmétiques ; en cas de chute importante, consultez un médecin ou un dermatologue.</p>
      </div>
    </section>
  );
}
